import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Camera, LineChart, RefreshCw, Wallet } from 'lucide-react';
import { useAccounts } from '../hooks';
import {
  getHistoriqueSoldes,
  HistoriqueSoldePoint,
} from '../lib/services/SoldeHistoryService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

type PeriodKey = '3m' | '6m' | '1y' | 'all';

const PERIODS: { key: PeriodKey; label: string; days: number | null }[] = [
  { key: '3m', label: '3 mois', days: 90 },
  { key: '6m', label: '6 mois', days: 180 },
  { key: '1y', label: '1 an', days: 365 },
  { key: 'all', label: 'Tout', days: null },
];

const FALLBACK_COLORS = [
  '#60a5fa',
  '#34d399',
  '#f472b6',
  '#fbbf24',
  '#a78bfa',
  '#fb7185',
  '#2dd4bf',
  '#f97316',
];

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatAxis(amount: number) {
  if (Math.abs(amount) >= 1000) return `${(amount / 1000).toFixed(1)}k€`;
  return `${amount.toFixed(0)}€`;
}

function formatDateLabel(iso: string) {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y.slice(2)}`;
}

type SeriesBuild = {
  compteId: string;
  nom: string;
  color: string;
  byDate: Map<string, number>;
};

function buildSeries(
  points: HistoriqueSoldePoint[],
  comptes: { id?: string; nom: string; solde: number; couleur?: string }[],
): SeriesBuild[] {
  const byCompte = new Map<string, SeriesBuild>();

  for (const p of points) {
    let s = byCompte.get(p.compte_id);
    if (!s) {
      const compte = comptes.find((c) => c.id === p.compte_id);
      const idx = byCompte.size;
      s = {
        compteId: p.compte_id,
        nom: p.compte_nom || compte?.nom || 'Compte',
        color: compte?.couleur || FALLBACK_COLORS[idx % FALLBACK_COLORS.length]!,
        byDate: new Map(),
      };
      byCompte.set(p.compte_id, s);
    }
    s.byDate.set(p.date_snapshot, p.solde);
  }

  // Point du jour = solde courant (prolongation jusqu'à aujourd'hui)
  const today = todayISO();
  for (const c of comptes) {
    if (!c.id) continue;
    let s = byCompte.get(c.id);
    if (!s) {
      if (points.length === 0) continue;
      const idx = byCompte.size;
      s = {
        compteId: c.id,
        nom: c.nom,
        color: c.couleur || FALLBACK_COLORS[idx % FALLBACK_COLORS.length]!,
        byDate: new Map(),
      };
      byCompte.set(c.id, s);
    }
    s.byDate.set(today, c.solde || 0);
  }

  return Array.from(byCompte.values()).sort((a, b) =>
    a.nom.localeCompare(b.nom, 'fr', { sensitivity: 'base' }),
  );
}

/** Remplit chaque date avec le dernier solde connu (plateau entre changements). */
function forwardFill(dates: string[], byDate: Map<string, number>): (number | null)[] {
  let last: number | null = null;
  return dates.map((d) => {
    if (byDate.has(d)) last = byDate.get(d)!;
    return last;
  });
}

const SoldeEvolution: React.FC = () => {
  const { comptes, loading: accountsLoading } = useAccounts();
  const [period, setPeriod] = useState<PeriodKey>('6m');
  const [points, setPoints] = useState<HistoriqueSoldePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [showTotal, setShowTotal] = useState(true);

  const range = useMemo(() => {
    const to = todayISO();
    const def = PERIODS.find((p) => p.key === period);
    const from = def?.days != null ? daysAgoISO(def.days) : '2000-01-01';
    return { from, to };
  }, [period]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await getHistoriqueSoldes({
      from: range.from,
      to: range.to,
    });
    if (err) {
      setError(err.message || 'Impossible de charger l’historique');
      setPoints([]);
    } else {
      setPoints(data);
    }
    setLoading(false);
  }, [range.from, range.to]);

  useEffect(() => {
    void load();
  }, [load]);

  const series = useMemo(() => buildSeries(points, comptes), [points, comptes]);

  const dates = useMemo(() => {
    const set = new Set<string>();
    for (const s of series) {
      for (const d of s.byDate.keys()) {
        if (d >= range.from && d <= range.to) set.add(d);
      }
    }
    return Array.from(set).sort();
  }, [series, range.from, range.to]);

  const visibleSeries = useMemo(
    () => series.filter((s) => !hiddenIds.has(s.compteId)),
    [series, hiddenIds],
  );

  const chartData = useMemo(() => {
    const datasets = visibleSeries.map((s) => ({
      label: s.nom,
      data: forwardFill(dates, s.byDate),
      borderColor: s.color,
      backgroundColor: s.color + '33',
      borderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 5,
      tension: 0,
      stepped: 'after' as const,
      spanGaps: false,
    }));

    if (showTotal && visibleSeries.length > 0 && dates.length > 0) {
      const filled = visibleSeries.map((s) => forwardFill(dates, s.byDate));
      const total = dates.map((_, i) => {
        let sum = 0;
        let any = false;
        for (const row of filled) {
          const v = row[i];
          if (v != null) {
            sum += v;
            any = true;
          }
        }
        return any ? sum : null;
      });
      datasets.unshift({
        label: 'Total',
        data: total,
        borderColor: '#e2e8f0',
        backgroundColor: '#e2e8f033',
        borderWidth: 2.5,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0,
        stepped: 'after' as const,
        spanGaps: false,
      });
    }

    return {
      labels: dates.map(formatDateLabel),
      datasets,
    };
  }, [visibleSeries, dates, showTotal]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: { color: '#cbd5e1', usePointStyle: true, padding: 16 },
        },
        tooltip: {
          callbacks: {
            label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) => {
              const v = ctx.parsed.y;
              if (v == null) return `${ctx.dataset.label}: —`;
              return `${ctx.dataset.label}: ${formatCurrency(v)}`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: { color: '#94a3b8', maxRotation: 45, autoSkip: true, maxTicksLimit: 12 },
          grid: { color: 'rgba(148,163,184,0.08)' },
        },
        y: {
          ticks: {
            color: '#94a3b8',
            callback: (tickValue: string | number) =>
              typeof tickValue === 'number' ? formatAxis(tickValue) : tickValue,
          },
          grid: { color: 'rgba(148,163,184,0.12)' },
        },
      },
      interaction: { mode: 'index' as const, intersect: false },
    }),
    [],
  );

  const lastTotals = useMemo(() => {
    return visibleSeries.map((s) => {
      const vals = [...s.byDate.entries()].sort((a, b) => a[0].localeCompare(b[0]));
      const last = vals[vals.length - 1];
      return { nom: s.nom, color: s.color, solde: last?.[1] ?? 0, date: last?.[0] };
    });
  }, [visibleSeries]);

  const toggleCompte = (id: string) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const busy = loading || accountsLoading;
  const hasHistory = points.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Évolution des soldes</h2>
          <p className="text-dark-400 text-sm mt-1">
            Courbes basées sur les instantanés enregistrés depuis Comptes
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-dark-600">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  period === p.key
                    ? 'bg-primary-500 text-white'
                    : 'bg-dark-800 text-dark-300 hover:text-white'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={busy}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dark-600 bg-dark-800 text-white text-sm hover:bg-dark-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {busy ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
            <p className="text-white mt-4">Chargement…</p>
          </div>
        </div>
      ) : !hasHistory ? (
        <div className="card text-center py-12 px-6">
          <LineChart className="w-14 h-14 text-dark-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Aucun historique pour l’instant</h3>
          <p className="text-dark-400 text-sm max-w-md mx-auto mb-6">
            Enregistre les soldes depuis la page Comptes. Seuls les changements sont stockés ;
            la courbe prolongera le dernier niveau connu.
          </p>
          <Link
            to="/accounts"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            <Camera className="w-4 h-4" />
            Aller enregistrer les soldes
          </Link>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-dark-800 border border-dark-600 text-sm text-white cursor-pointer">
              <input
                type="checkbox"
                checked={showTotal}
                onChange={(e) => setShowTotal(e.target.checked)}
                className="rounded border-dark-600 bg-dark-900 text-primary-500 focus:ring-primary-500"
              />
              Total
            </label>
            {series.map((s) => {
              const on = !hiddenIds.has(s.compteId);
              return (
                <button
                  key={s.compteId}
                  type="button"
                  onClick={() => toggleCompte(s.compteId)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                    on
                      ? 'border-dark-500 bg-dark-800 text-white'
                      : 'border-dark-700 bg-dark-900/50 text-dark-500'
                  }`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: on ? s.color : '#475569' }}
                  />
                  {s.nom}
                </button>
              );
            })}
          </div>

          <div className="card">
            <div className="h-80 sm:h-96">
              {dates.length < 1 ? (
                <div className="h-full flex items-center justify-center text-dark-400 text-sm">
                  Pas assez de points sur cette période.
                </div>
              ) : (
                <Line data={chartData} options={chartOptions} />
              )}
            </div>
            <p className="text-xs text-dark-500 mt-3 text-center">
              Courbes en paliers : un point = un changement de solde enregistré
              {dates.length > 0 ? ` · ${dates.length} date${dates.length > 1 ? 's' : ''}` : ''}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {lastTotals.map((t) => (
              <div
                key={t.nom}
                className="bg-dark-800 border border-dark-700 rounded-xl px-4 py-3 flex items-center gap-3"
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: t.color + '22' }}
                >
                  <Wallet className="w-4 h-4" style={{ color: t.color }} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-dark-400 truncate">{t.nom}</p>
                  <p className="text-lg font-semibold text-white">{formatCurrency(t.solde)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/accounts"
              className="inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300"
            >
              <Camera className="w-4 h-4" />
              Enregistrer un nouvel instantané
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default SoldeEvolution;
